import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import './AddProductPanel.css';
import { compressImageFile } from '../utils/imageCompression';

function AddProductPanel({ open, onClose, onSuccess, authedFetch, printSticker }) {
  const { currentUser, canPerformAction } = useAuth();
  const apiBase =
    typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:3001'
      : '';

  const [allSpecs, setAllSpecs] = useState([]);
  const [isLoadingSpecs, setIsLoadingSpecs] = useState(true);

  const [productType, setProductType] = useState('');
  const [selectedQuality, setSelectedQuality] = useState('');
  const [selectedFabric, setSelectedFabric] = useState('');
  const [selectedWidth, setSelectedWidth] = useState('');
  const [designNumber, setDesignNumber] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [fabricPhotos, setFabricPhotos] = useState([]); // Array of { file, preview }
  const [scaleReferencePhoto, setScaleReferencePhoto] = useState(null); // { file, preview }
  const [sheerReferencePhoto, setSheerReferencePhoto] = useState(null); // { file, preview }

  const [openingLength, setOpeningLength] = useState('');
  const [previewPieceId, setPreviewPieceId] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  const fetchAllSpecs = useCallback(async () => {
    setIsLoadingSpecs(true);
    setError('');
    try {
      const res = await authedFetch('/api/v1/add-product/fabric-specs');
      const data = await res.json();
      if (data.success) setAllSpecs(data.specifications);
      else setError(data.message || 'Failed to load fabric specifications.');
    } catch { setError('Failed to load fabric specifications.'); }
    finally { setIsLoadingSpecs(false); }
  }, [authedFetch]);

  const fetchLastBatchNumber = useCallback(async () => {
    try {
      const res = await authedFetch('/api/v1/add-product/last-batch-number');
      const data = await res.json();
      if (data.success && data.lastBatchNumber) setBatchNumber(data.lastBatchNumber);
    } catch { /* best-effort */ }
  }, [authedFetch]);

  const fetchPreviewPieceId = useCallback(async () => {
    try {
      const res = await authedFetch('/api/v1/add-product/preview-piece-ids?count=1');
      const data = await res.json();
      if (data.success && data.pieceIds.length > 0) setPreviewPieceId(data.pieceIds[0]);
    } catch { /* best-effort */ }
  }, [authedFetch]);

  useEffect(() => {
    if (open) {
      fetchAllSpecs();
      fetchLastBatchNumber();
    }
  }, [open, fetchAllSpecs, fetchLastBatchNumber]);

  const resetForm = () => {
    setProductType('');
    setSelectedQuality('');
    setSelectedFabric('');
    setSelectedWidth('');
    setDesignNumber('');
    setOpeningLength('');
    setPreviewPieceId('');
    setFabricPhotos([]);
    setScaleReferencePhoto(null);
    setSheerReferencePhoto(null);
    setError('');
    setSuccess('');
    setDuplicateInfo(null);
  };

  const productTypes = useMemo(() => {
    return [...new Set(allSpecs.map(s => s.type))].filter(Boolean).sort();
  }, [allSpecs]);

  const specs = useMemo(() => {
    if (!productType) return [];
    return allSpecs.filter(s => s.type === productType);
  }, [allSpecs, productType]);

  useEffect(() => {
    setSelectedQuality('');
    setSelectedFabric('');
    setSelectedWidth('');
    setDesignNumber('');
    setOpeningLength('');
    setPreviewPieceId('');
    setSuccess('');
  }, [productType]);

  useEffect(() => { setSelectedFabric(''); setSelectedWidth(''); }, [selectedQuality]);
  useEffect(() => { setSelectedWidth(''); }, [selectedFabric]);

  const uniqueQualities = useMemo(() => {
    return [...new Set(specs.map(s => s.quality))].filter(Boolean).sort();
  }, [specs]);

  const uniqueFabrics = useMemo(() => {
    if (!selectedQuality) return [];
    return [...new Set(specs.filter(s => s.quality === selectedQuality).map(s => s.fabric))].filter(Boolean).sort();
  }, [specs, selectedQuality]);

  const uniqueWidths = useMemo(() => {
    if (!selectedQuality || !selectedFabric) return [];
    return [...new Set(specs.filter(s => s.quality === selectedQuality && s.fabric === selectedFabric).map(s => s.width))].filter(Boolean).sort();
  }, [specs, selectedQuality, selectedFabric]);

  const matchedSpec = useMemo(() => {
    if (!selectedQuality || !selectedFabric || !selectedWidth) return null;
    return specs.find(s => s.quality === selectedQuality && s.fabric === selectedFabric && s.width === selectedWidth) || null;
  }, [specs, selectedQuality, selectedFabric, selectedWidth]);

  const generatedSku = useMemo(() => {
    if (!matchedSpec || !designNumber.trim()) return '';
    const prefix = (matchedSpec.skuPrefix || '').trim();
    const dn = designNumber.replace(/\s/g, '').toUpperCase();
    const suffix = (matchedSpec.skuSuffix || '').trim();
    return `${prefix}-0-${dn}-${suffix}`.replace(/\s/g, '');
  }, [matchedSpec, designNumber]);

  useEffect(() => {
    if (generatedSku) fetchPreviewPieceId();
    else setPreviewPieceId('');
  }, [generatedSku, fetchPreviewPieceId]);

  const handlePhotosChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setError('');
    const newPhotos = [];
    let processed = 0;

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPhotos.push({ file, preview: reader.result });
        processed++;
        if (processed === files.length) {
          setFabricPhotos(prev => {
            const combined = [...prev, ...newPhotos];
            if (combined.length > 6) {
              setError('You can upload a maximum of 6 reference photos.');
              return combined.slice(0, 6);
            }
            return combined;
          });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFabricPhoto = (index) => {
    setFabricPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleScaleReferenceChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    const reader = new FileReader();
    reader.onloadend = () => setScaleReferencePhoto({ file, preview: reader.result });
    reader.readAsDataURL(file);
  };

  const handleSheerReferenceChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    const reader = new FileReader();
    reader.onloadend = () => setSheerReferencePhoto({ file, preview: reader.result });
    reader.readAsDataURL(file);
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const submitProduct = async (forceOverride = false) => {
    if (!generatedSku || !matchedSpec) return;

    const lengthVal = parseFloat(openingLength) || 0;
    if (lengthVal < 0) {
      setError('Length cannot be negative.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');
    setDuplicateInfo(null);

    try {
      const token = await currentUser.getIdToken();
      let fabricPhotoUrl = null;
      let fabricPhotoPath = null;
      const uploadedPhotos = [];
      let uploadedScaleReferencePhoto = null;
      let uploadedSheerReferencePhoto = null;

      if (fabricPhotos.length > 0) {
        await Promise.all(fabricPhotos.map(async ({ file }) => {
          const compressed = await compressImageFile(file);
          const base64Image = await fileToBase64(compressed);
          const uploadRes = await fetch(`${apiBase}/api/v1/add-product/upload-fabric-photo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ base64Image, mimeType: compressed.type, sku: generatedSku }),
          });
          const uploadData = await uploadRes.json();
          if (uploadData.success) {
            uploadedPhotos.push({ url: uploadData.url, path: uploadData.path });
          } else {
            throw new Error(uploadData.message || 'Failed to upload a reference photo.');
          }
        }));

        if (uploadedPhotos.length > 0) {
          fabricPhotoUrl = uploadedPhotos[0].url;
          fabricPhotoPath = uploadedPhotos[0].path;
        }
      }

      if (scaleReferencePhoto?.file) {
        const compressed = await compressImageFile(scaleReferencePhoto.file);
        const base64Image = await fileToBase64(compressed);
        const uploadRes = await fetch(`${apiBase}/api/v1/add-product/upload-fabric-photo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ base64Image, mimeType: compressed.type, sku: generatedSku }),
        });
        const uploadData = await uploadRes.json();
        if (uploadData.success) {
          uploadedScaleReferencePhoto = { url: uploadData.url, path: uploadData.path };
        } else {
          throw new Error(uploadData.message || 'Failed to upload scale reference photo.');
        }
      }

      if (sheerReferencePhoto?.file) {
        const compressed = await compressImageFile(sheerReferencePhoto.file);
        const base64Image = await fileToBase64(compressed);
        const uploadRes = await fetch(`${apiBase}/api/v1/add-product/upload-fabric-photo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ base64Image, mimeType: compressed.type, sku: generatedSku }),
        });
        const uploadData = await uploadRes.json();
        if (uploadData.success) {
          uploadedSheerReferencePhoto = { url: uploadData.url, path: uploadData.path };
        } else {
          throw new Error(uploadData.message || 'Failed to upload sheer reference photo.');
        }
      }

      const res = await authedFetch('/api/v1/add-product/opening-stock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sku: generatedSku,
          fabricSpecId: matchedSpec.id,
          pieces: lengthVal > 0 ? [{ length: lengthVal }] : [],
          batchNumber,
          designNumber,
          fabricPhotoUrl,
          fabricPhotoPath,
          fabricPhotos: uploadedPhotos,
          scaleReferencePhoto: uploadedScaleReferencePhoto,
          sheerReferencePhoto: uploadedSheerReferencePhoto,
          force: forceOverride,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setSuccess(data.pieceIds.length > 0 ? `${data.message} Piece ID: ${data.pieceIds[0]}` : data.message);
        if (printSticker && data.pieceIds?.length > 0) {
          const lengthVal = parseFloat(openingLength) || 0;
          data.pieceIds.forEach((pid) => {
            printSticker({
              line1: generatedSku,
              line2: lengthVal > 0 ? `${pid} • ${lengthVal}m` : pid,
              line3: '',
              barcodeText: generatedSku,
            })?.catch?.(() => {});
          });
        }
        setOpeningLength('');
        setPreviewPieceId('');
        setDesignNumber('');
        setFabricPhotos([]);
        setScaleReferencePhoto(null);
        setSheerReferencePhoto(null);
        if (onSuccess) onSuccess();
      } else if (data.duplicate) {
        setDuplicateInfo(data);
      } else {
        setError(data.message || 'Failed to create opening stock.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to create opening stock.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    submitProduct(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const canSubmit = canPerformAction('addProduct.submit');

  if (!open) return null;

  return (
    <>
      <div className="ap-overlay" onClick={handleClose} />
      <div className="ap-panel">
        <div className="ap-panel-header">
          <h2>Add New Product</h2>
          <button className="ap-close-btn" onClick={handleClose}>&times;</button>
        </div>

        <div className="ap-panel-body">
          {error && <div className="ap-error">{error}</div>}
          {success && <div className="ap-success">{success}</div>}

          {duplicateInfo && (
            <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 6, fontSize: '0.9rem' }}>
                SKU Already Exists
              </div>
              <div style={{ fontSize: '0.82rem', color: '#78350f', marginBottom: 8 }}>
                {duplicateInfo.message}
                {duplicateInfo.source === 'new_products' && duplicateInfo.existingBatch && (
                  <span> (Batch: {duplicateInfo.existingBatch})</span>
                )}
                {duplicateInfo.existingCreatedAt && (
                  <span> — Added {new Date(duplicateInfo.existingCreatedAt).toLocaleDateString()}</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => submitProduct(true)}
                  disabled={isSubmitting}
                  style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600 }}
                >
                  {isSubmitting ? 'Overwriting...' : 'Overwrite & Replace'}
                </button>
                <button
                  type="button"
                  onClick={() => setDuplicateInfo(null)}
                  style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="ap-section">
            <label className="ap-label">Batch Number</label>
            <input type="text" value={batchNumber} onChange={e => setBatchNumber(e.target.value)} placeholder="e.g. B-2024-001" />
          </div>

          <div className="ap-section">
            <label className="ap-label">Product Type</label>
            {isLoadingSpecs ? <p>Loading...</p> : (
              <select value={productType} onChange={e => setProductType(e.target.value)}>
                <option value="">-- Select --</option>
                {productTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
          </div>

          {productType && specs.length > 0 && (
            <div className="ap-section">
              <label className="ap-label">Quality</label>
              <select value={selectedQuality} onChange={e => setSelectedQuality(e.target.value)} disabled={uniqueQualities.length === 0}>
                <option value="">-- Select Quality --</option>
                {uniqueQualities.map(q => <option key={q} value={q}>{q}</option>)}
              </select>

              {selectedQuality && (
                <>
                  <label className="ap-label">Fabric</label>
                  <select value={selectedFabric} onChange={e => setSelectedFabric(e.target.value)} disabled={uniqueFabrics.length === 0}>
                    <option value="">-- Select Fabric --</option>
                    {uniqueFabrics.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </>
              )}

              {selectedFabric && (
                <>
                  <label className="ap-label">Width</label>
                  <select value={selectedWidth} onChange={e => setSelectedWidth(e.target.value)} disabled={uniqueWidths.length === 0}>
                    <option value="">-- Select Width --</option>
                    {uniqueWidths.map(w => <option key={w} value={w}>{w}</option>)}
                  </select>
                </>
              )}
            </div>
          )}

          {matchedSpec && (
            <div className="ap-section">
              <label className="ap-label">Design Number</label>
              <input type="text" value={designNumber} onChange={e => setDesignNumber(e.target.value.toUpperCase().replace(/\s/g, ''))} placeholder="e.g. TA6767A" />

              <label className="ap-label">Fabric Reference Photos (Upload 3-6 photos)</label>
              <input type="file" accept="image/*" multiple onChange={handlePhotosChange} />
              <p className="ap-photo-limit-text">Upload front, back, scale, drape etc. for accurate AI generation.</p>
              {fabricPhotos.length > 0 && (
                <div className="ap-photos-grid">
                  {fabricPhotos.map((photo, index) => (
                    <div key={index} className="ap-photo-container">
                      <img src={photo.preview} alt={`Fabric Swatch ${index + 1}`} className="ap-photo-thumbnail" />
                      <button type="button" className="ap-photo-remove-btn" onClick={() => removeFabricPhoto(index)} title="Remove photo">&times;</button>
                    </div>
                  ))}
                </div>
              )}

              <label className="ap-label">Scale Reference Photo (with measuring tape)</label>
              <input type="file" accept="image/*" onChange={handleScaleReferenceChange} />
              <p className="ap-photo-limit-text">Upload one photo where the measuring tape/ruler is visible on the fabric. AI will use this for motif size.</p>
              {scaleReferencePhoto && (
                <div className="ap-photos-grid">
                  <div className="ap-photo-container">
                    <img src={scaleReferencePhoto.preview} alt="Scale reference" className="ap-photo-thumbnail" />
                    <button type="button" className="ap-photo-remove-btn" onClick={() => setScaleReferencePhoto(null)} title="Remove scale reference">&times;</button>
                  </div>
                </div>
              )}

              <label className="ap-label">Sheer / Transparency Reference Photo</label>
              <input type="file" accept="image/*" onChange={handleSheerReferenceChange} />
              <p className="ap-photo-limit-text">Upload one photo with a hand or dark card behind a single fabric layer. AI will use this to preserve true transparency.</p>
              {sheerReferencePhoto && (
                <div className="ap-photos-grid">
                  <div className="ap-photo-container">
                    <img src={sheerReferencePhoto.preview} alt="Sheer reference" className="ap-photo-thumbnail" />
                    <button type="button" className="ap-photo-remove-btn" onClick={() => setSheerReferencePhoto(null)} title="Remove sheer reference">&times;</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {generatedSku && (
            <div className="ap-sku-display">
              <span className="ap-sku-label">SKU:</span>
              <span className="ap-sku-value">{generatedSku}</span>
            </div>
          )}

          {generatedSku && (
            <form onSubmit={handleSubmit}>
              <div className="ap-section">
                <label className="ap-label">Piece ID (estimated)</label>
                <input type="text" value={previewPieceId || '...'} disabled />

                <label className="ap-label">Length (meters)</label>
                <input type="number" step="0.1" min="0" value={openingLength} onChange={e => setOpeningLength(e.target.value)} placeholder="e.g. 5.5" />

                <p className="ap-note">Piece ID is an estimate. Actual ID assigned on submission.</p>

                {canSubmit && (
                  <button type="submit" className="ap-submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Add New Product'}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}

export default AddProductPanel;
